import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IconArrowUpRight,
  IconCode,
  IconDeviceMobile,
  IconMovie,
  IconSparkles,
  IconSpeakerphone,
} from "@tabler/icons-react";

export default function TechnologyHero() {
  return (
    <section
      className="
        px-[14px]
        pb-8
        pt-[115px]
        sm:px-5
        lg:pt-[125px]
      "
    >
      <div
        className="
          relative
          mx-auto
          min-h-[700px]
          max-w-[1320px]
          overflow-hidden
          rounded-[20px]
          bg-white
        "
      >
        {/* BLUE CORNER */}

        <div
          className="
            pointer-events-none
            absolute
            left-0
            top-0
            h-[190px]
            w-[340px]
          "
        >
          <div
            className="
              absolute
              left-0
              top-0
              h-[2px]
              w-full
              bg-gradient-to-r
              from-[#4aa8ff]
              via-[#79c1ff]
              to-transparent
            "
          />

          <div
            className="
              absolute
              left-0
              top-0
              h-full
              w-[2px]
              bg-gradient-to-b
              from-[#4aa8ff]
              via-[#79c1ff]
              to-transparent
            "
          />
        </div>

        {/* GLOW */}

        <div
          className="
            pointer-events-none
            absolute
            -left-32
            -top-32
            h-[520px]
            w-[520px]
            rounded-full
          "
          style={{
            background:
              "radial-gradient(circle, rgba(85,174,243,0.16), transparent 70%)",
          }}
        />

        {/* RIGHT GLOW */}

        <div
          className="
            pointer-events-none
            absolute
            -bottom-40
            -right-40
            h-[500px]
            w-[500px]
            rounded-full
          "
          style={{
            background:
              "radial-gradient(circle, rgba(85,174,243,0.12), transparent 70%)",
          }}
        />

        <div
          className="
            relative
            z-10
            grid
            min-h-[700px]
            items-center
            gap-12
            px-6
            py-20
            sm:px-10
            lg:grid-cols-[1fr_0.9fr]
            lg:px-16
          "
        >
          {/* HERO CONTENT */}

          <div>
            <motion.div
              initial={{
                opacity: 0,
                y: 15,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.6,
              }}
              className="
                mb-6
                inline-flex
                items-center
                gap-2
                rounded-full
                border
                border-blue-100
                bg-blue-50
                px-4
                py-2
                text-[11px]
                font-bold
                uppercase
                tracking-[0.18em]
                text-[#3899e4]
              "
            >
              <span
                className="
                  h-2
                  w-2
                  animate-pulse
                  rounded-full
                  bg-[#55aef3]
                "
              />
              WE3X Technologies
            </motion.div>

            <motion.h1
              initial={{
                opacity: 0,
                y: 35,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.85,
                delay: 0.1,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="
                merriweather
                max-w-[720px]
                text-[43px]
                font-black
                leading-[1.03]
                tracking-[-0.045em]
                sm:text-[54px]
                lg:text-[62px]
                xl:text-[68px]
              "
            >
              We turn ideas
              <span className="block text-[#55aef3]">
                into digital experiences.
              </span>
            </motion.h1>

            <motion.p
              initial={{
                opacity: 0,
                y: 25,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.8,
                delay: 0.2,
              }}
              className="
                mt-7
                max-w-[650px]
                text-[15px]
                leading-8
                text-neutral-600
                sm:text-[16px]
              "
            >
              From custom software and high-converting landing pages to mobile
              apps, digital campaigns and cinematic websites, WE3X
              Technologies helps businesses create stronger digital
              experiences and move forward with confidence.
            </motion.p>

            <motion.div
              initial={{
                opacity: 0,
                y: 20,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              transition={{
                duration: 0.8,
                delay: 0.3,
              }}
              className="
                mt-9
                flex
                flex-wrap
                gap-3
              "
            >
              <Link
                to="/contact"
                className="
                  group
                  inline-flex
                  items-center
                  gap-2
                  rounded-[9px]
                  bg-[#151515]
                  px-7
                  py-3.5
                  text-sm
                  font-semibold
                  text-white
                  transition-all
                  hover:-translate-y-1
                  hover:bg-[#55aef3]
                "
              >
                Start Your Project
                <IconArrowUpRight
                  size={17}
                  className="
                    transition-transform
                    group-hover:translate-x-1
                    group-hover:-translate-y-1
                  "
                />
              </Link>

              <a
                href="#services"
                className="
                  rounded-[9px]
                  border
                  border-neutral-300
                  bg-white
                  px-7
                  py-3.5
                  text-sm
                  font-semibold
                  transition
                  hover:border-neutral-950
                "
              >
                Explore Our Work
              </a>
            </motion.div>

            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              transition={{
                duration: 1,
                delay: 0.5,
              }}
              className="
                mt-8
                flex
                flex-wrap
                gap-x-6
                gap-y-3
              "
            >
              <HeroPoint text="Strategy" />
              <HeroPoint text="Design" />
              <HeroPoint text="Development" />
              <HeroPoint text="Growth" />
            </motion.div>
          </div>

          {/* HERO VISUAL */}

          <motion.div
            initial={{
              opacity: 0,
              scale: 0.93,
              x: 40,
            }}
            animate={{
              opacity: 1,
              scale: 1,
              x: 0,
            }}
            transition={{
              duration: 1,
              delay: 0.15,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="
              relative
              mx-auto
              w-full
              max-w-[540px]
            "
          >
            <div
              className="
                relative
                overflow-hidden
                rounded-[24px]
                border
                border-neutral-200
                bg-[#101010]
                p-4
                shadow-2xl
                shadow-black/[0.08]
              "
            >
              {/* BROWSER TOP */}

              <div
                className="
                  flex
                  items-center
                  justify-between
                  border-b
                  border-white/10
                  pb-4
                "
              >
                <div className="flex gap-2">
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                  <span className="h-2.5 w-2.5 rounded-full bg-white/20" />
                </div>

                <span className="text-[10px] text-white/30">
                  we3x.digital
                </span>
              </div>

              {/* SCREEN */}

              <div className="relative min-h-[390px] pt-5">
                <motion.div
                  animate={{
                    y: [0, -7, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="
                    rounded-[17px]
                    bg-white
                    p-5
                  "
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p
                        className="
                          text-[9px]
                          font-bold
                          uppercase
                          tracking-[0.18em]
                          text-[#55aef3]
                        "
                      >
                        Digital product
                      </p>

                      <div className="mt-3 h-4 w-36 rounded bg-neutral-900" />
                    </div>

                    <IconSparkles size={25} className="text-[#55aef3]" />
                  </div>

                  <div
                    className="
                      mt-7
                      grid
                      grid-cols-2
                      gap-3
                    "
                  >
                    <HeroVisualBox
                      icon={<IconCode size={20} />}
                      title="Software"
                    />

                    <HeroVisualBox
                      icon={<IconDeviceMobile size={20} />}
                      title="Apps"
                    />

                    <HeroVisualBox
                      icon={<IconSpeakerphone size={20} />}
                      title="Marketing"
                    />

                    <HeroVisualBox
                      icon={<IconMovie size={20} />}
                      title="Creative"
                    />
                  </div>

                  <div
                    className="
                      mt-4
                      rounded-xl
                      bg-[#f6f6f6]
                      p-4
                    "
                  >
                    <div className="flex items-end gap-2">
                      {[42, 60, 48, 76, 62, 90, 74].map((height, index) => (
                        <motion.div
                          key={index}
                          initial={{
                            height: 0,
                          }}
                          animate={{
                            height,
                          }}
                          transition={{
                            duration: 0.8,
                            delay: 0.6 + index * 0.08,
                          }}
                          className="
                              flex-1
                              rounded-t
                              bg-[#55aef3]
                            "
                        />
                      ))}
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function HeroPoint({ text }) {
  return (
    <div
      className="
        flex
        items-center
        gap-2
        text-xs
        font-bold
        text-neutral-600
      "
    >
      <span
        className="
          h-1.5
          w-1.5
          rounded-full
          bg-[#55aef3]
        "
      />

      {text}
    </div>
  );
}

function HeroVisualBox({ icon, title }) {
  return (
    <div
      className="
        rounded-xl
        border
        border-neutral-200
        bg-[#f8f8f8]
        p-4
      "
    >
      <div className="text-[#55aef3]">{icon}</div>

      <p className="mt-3 text-xs font-bold">{title}</p>
    </div>
  );
}
